/**
 * Safe wrapper for Notification API to handle iOS Safari and other browsers
 * that don't support the Web Notifications API
 */

interface NotificationPermission {
  state: 'default' | 'granted' | 'denied' | 'not-supported';
}

class NotificationWrapper {
  private static isSupported: boolean = typeof window !== 'undefined' && 'Notification' in window;
  
  /**
   * Check if notifications are supported in the current browser
   */
  static isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current notification permission state
   */
  static getPermission(): NotificationPermission['state'] {
    if (!this.isSupported) {
      return 'not-supported';
    }
    
    try {
      return (window as Window & { Notification: { permission: NotificationPermission } }).Notification.permission as NotificationPermission['state'];
    } catch (error) {
      console.warn('Error accessing Notification.permission:', error);
      return 'not-supported';
    }
  }

  /**
   * Request notification permission with iOS Safari handling
   */
  static async requestPermission(): Promise<NotificationPermission['state']> {
    if (!this.isSupported) {
      console.log('Notifications not supported on this device/browser');
      return 'not-supported';
    }

    try {
      const permission = await (window as Window & { Notification: { requestPermission: () => Promise<NotificationPermission> } }).Notification.requestPermission();
      return permission as NotificationPermission['state'];
    } catch (error) {
      console.warn('Error requesting notification permission:', error);
      return 'not-supported';
    }
  }

  /**
   * Check if we can request notification permission
   */
  static canRequestPermission(): boolean {
    if (!this.isSupported) {
      return false;
    }

    const currentPermission = this.getPermission();
    
    // Can't request if already denied (except on localhost)
    if (currentPermission === 'denied' && 
        !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      return false;
    }

    // Must be in secure context
    if (!window.isSecureContext) {
      return false;
    }

    return true;
  }

  /**
   * Log device/browser information for debugging
   */
  static logEnvironmentInfo(): void {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isIOSSafari = isIOS && isSafari;
    
    console.log('[NotificationWrapper] Environment Info:', {
      isNotificationSupported: this.isSupported,
      currentPermission: this.isSupported ? this.getPermission() : 'not-supported',
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      userAgent: userAgent,
      isIOS,
      isSafari,
      isIOSSafari,
      hasPushManager: 'PushManager' in window,
      hasServiceWorker: 'serviceWorker' in navigator
    });

    if (isIOSSafari && !this.isSupported) {
      console.log('[NotificationWrapper] iOS Safari detected without Notification API support.');
      console.log('[NotificationWrapper] Push notifications are not supported on iOS Safari.');
      console.log('[NotificationWrapper] Consider using in-app notifications or native app for push support.');
    }
  }
}

export default NotificationWrapper;